import classNames from 'classnames';
import { useState } from 'react';
import type React from 'react';
import {
  getAccessibleTabIndex,
  getAriaButtonRoleAttribute,
  handleAccessibleButtonKeyDown,
} from '../Button/ariaHelpers';
import { Icon } from '../Icon/Icon';
import type { IconGlyph } from '../Icon/IconGlyph';
import type { SubmenuItemTree } from './MenuItem.types';
import { getSubMenuItemClassNames } from './SubMenuItem.styles';
import { renderIcon } from './helpers/helper';

export interface SubmenuItemProps {
  item: SubmenuItemTree;
  disabled?: boolean;
  leadingIcon?: IconGlyph;
  trailingIcon?: IconGlyph;
  size?: 'small' | 'default';
  selected?: boolean;
  label?: string;
  /**
   * When omitted, the arrow is shown if the item has children or is open.
   */
  showArrow?: boolean;
}

export const SubmenuItem = ({
  item,
  label = item.label,
  disabled,
  leadingIcon,
  trailingIcon,
  selected,
  size = 'default',
  showArrow: showArrowArgument,
}: SubmenuItemProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const expandable = (item.children && item.children.length > 0) ?? false;
  const showArrow = showArrowArgument ?? (expandable || isOpen);
  const renderArrow = () => {
    if (!showArrow) {
      return null;
    }

    if (!expandable) {
      return <Icon size='sm' glyph='fa-angle-right' className='shrink-0' />;
    }

    if (isOpen) {
      return <Icon size='sm' glyph='fa-angle-up' className='shrink-0' />;
    }

    return <Icon size='sm' glyph='fa-angle-down' className='shrink-0' />;
  };

  const renderTrailingIcon = () =>
    renderIcon(trailingIcon) || renderIcon(item.iconClassName);
  const renderLeadingIcon = () => renderIcon(leadingIcon);

  const handleClick = (e: React.MouseEvent) => {
    // Stop the event from propagating to the parent MenuItem/SubmenuItem
    e.stopPropagation();

    // Toggle the submenu if it is expandable and not disabled
    if (expandable && !disabled) {
      //e.stopPropagation(); // Prevent event bubbling to parent SubmenuItem
      setIsOpen(!isOpen);
    }

    item.onClick?.(e);
  };

  return (
    <div
      className={classNames(
        getSubMenuItemClassNames({
          size,
          isDisabled: disabled,
          selected,
          isOpen,
          expandable,
        }),
        !disabled && 'cursor-pointer',
      )}
      onClick={handleClick}
      {...getAriaButtonRoleAttribute()}
      tabIndex={getAccessibleTabIndex(disabled)}
      onKeyDown={(e) => handleAccessibleButtonKeyDown(e, disabled)}
    >
      <div className='flex gap-xs items-center'>
        {renderLeadingIcon()}
        <div className='flex-1'>{label}</div>
        {renderTrailingIcon()}
        {renderArrow()}
      </div>
      {expandable
        ? isOpen &&
          item.children?.map((child) => (
            <SubmenuItem
              key={child._key}
              item={child}
              leadingIcon={child.iconClassName}
              disabled={disabled}
              size={size}
            />
          ))
        : null}
    </div>
  );
};
